import { type MapDef, MapFormatError, parseCustomMap } from "../sim/maps";

/** A player-made map kept in this browser (P11.1). */
export interface StoredMap {
	id: string;
	map: MapDef;
	/** ISO date of the last save. */
	updated: string;
}

/** Custom maps kept in the browser; the oldest go first when full. */
export const MAX_CUSTOM_MAPS = 20;
const STORAGE_KEY = "gravity.maps.v1";

/** Custom maps in this browser, newest first. Broken or invalid entries are skipped. */
export function loadCustomMaps(): StoredMap[] {
	try {
		const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
		if (!Array.isArray(raw)) return [];
		return raw.flatMap((entry) => {
			if (typeof entry !== "object" || entry === null) return [];
			const e = entry as Record<string, unknown>;
			if (typeof e.id !== "string" || typeof e.updated !== "string") return [];
			try {
				return [{ id: e.id, updated: e.updated, map: parseCustomMap(e.map) }];
			} catch {
				return [];
			}
		});
	} catch {
		return [];
	}
}

/** Saves (or replaces, by id) a map as the newest. Returns false if storage refused it. */
export function saveCustomMap(entry: StoredMap): boolean {
	const others = loadCustomMaps().filter((m) => m.id !== entry.id);
	return write([entry, ...others].slice(0, MAX_CUSTOM_MAPS));
}

export function deleteCustomMap(id: string): void {
	write(loadCustomMaps().filter((m) => m.id !== id));
}

function write(list: StoredMap[]): boolean {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
		return true;
	} catch {
		return false;
	}
}

/** A fresh id for a new map (not part of the sim, so Math.random is fine). */
export function newMapId(): string {
	return `m${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
}

/** The starting point of a new map: one planet in the middle and four spawn points. */
export function blankMap(name = "New map"): MapDef {
	return {
		name,
		size: { w: 5000, h: 5000 },
		planets: [{ type: "earth", mass: 600, radius: 250, x: 2500, y: 2500 }],
		spawnPoints: [
			{ x: 800, y: 800 },
			{ x: 4200, y: 4200 },
			{ x: 4200, y: 800 },
			{ x: 800, y: 4200 },
		],
		fuelItems: [],
	};
}

/** The map as a JSON file (the same format as the built-in maps). */
export function exportMap(map: MapDef): string {
	return `${JSON.stringify(map, null, "\t")}\n`;
}

/** A map from a file's text, or an explanation of what is wrong with it. */
export function importMap(text: string): { map: MapDef } | { error: string } {
	try {
		return { map: parseCustomMap(JSON.parse(text)) };
	} catch (err) {
		if (err instanceof MapFormatError) return { error: err.message };
		return { error: "This isn't a Gravity map file (expected JSON)." };
	}
}

/** File name for an exported map, e.g. `gravity-map-twin-suns.json`. */
export function mapFileName(map: MapDef): string {
	const slug = map.name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");
	return `gravity-map-${slug || "custom"}.json`;
}
